/**
 * Changing plan, without leaving the app.
 *
 * This page used to redirect to /pricing, the public sales page. For someone
 * already signed in that means being thrown out of the dashboard onto a page
 * with no navigation, addressed to a visitor who has not bought anything — and
 * with no way back except the browser's own button. The plans are shown here
 * instead, in the app, with the plan they are already on marked as such.
 *
 * The prices come from the same /api/stripe/pricing endpoint the public page
 * reads, so the two cannot drift apart.
 */

import { RenewalTerms } from "@/components/subscription/RenewalTerms";
import {
  createCheckoutSession,
  detectCurrency,
  getPricingTiers,
  type Currency,
  type PricingTier,
  type StripeTierName,
} from "@/services/stripe";
import { useAuthStore } from "@/stores/authStore";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import { ArrowLeft, Check } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";

const MONTHLY_ORDER = ["FREE", "PLUS", "PREMIUM"];

export default function SubscriptionPlansPage() {
  const { token } = useAuthStore();
  const { currentSubscription, fetchSubscription } = useSubscriptionStore();
  const [tiers, setTiers] = useState<PricingTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [currency, setCurrency] = useState<Currency>(detectCurrency);

  const currentTier = currentSubscription?.tier || "FREE";
  const currencySymbol = currency === "eur" ? "€" : "$";

  useEffect(() => {
    if (token) fetchSubscription(token);
  }, [token, fetchSubscription]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPricingTiers(currency)
      .then((data) => {
        if (!cancelled) setTiers(data);
      })
      .catch(() => {
        if (!cancelled) toast.error("Plan prices could not be loaded.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currency]);

  const subscribe = async (tier: StripeTierName) => {
    if (!token) {
      toast.error("Please sign in again.");
      return;
    }
    setCheckoutLoading(tier);
    try {
      if (!(await createCheckoutSession(tier, token, currency)))
        setCheckoutLoading(null);
    } catch (error) {
      // The reason, when there is one. "Checkout could not be started" on its
      // own sent a real 400 from Stripe — an unsupported parameter — back as
      // something nobody could act on, and it took reading the server log to
      // find out what had actually happened.
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : "Checkout could not be started. Please try again.",
      );
      setCheckoutLoading(null);
    }
  };

  const monthly = MONTHLY_ORDER.map((name) =>
    tiers.find((tier) => tier.name === name),
  ).filter((tier): tier is PricingTier => Boolean(tier));
  const singleTrip = tiers.filter((tier) => tier.billingType === "one_time");

  const renderCard = (tier: PricingTier, suffix: string) => {
    const isCurrent = tier.name === currentTier;
    const isFree = tier.billingType === "free";

    return (
      <div
        key={tier.id}
        className={`flex flex-col rounded-xl border bg-white p-6 ${
          isCurrent ? "border-blue-500 ring-1 ring-blue-500" : "border-gray-200"
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-lg font-semibold text-gray-900">
            {tier.displayName || tier.name}
          </h3>
          {isCurrent && (
            <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
              Your plan
            </span>
          )}
        </div>

        <p className="mt-3">
          <span className="text-3xl font-bold tabular-nums text-gray-900">
            {currencySymbol}
            {tier.price}
          </span>
          <span className="ml-1 text-sm text-gray-500">{suffix}</span>
        </p>

        <ul className="mt-5 flex-1 space-y-2">
          {tier.features.map((feature, index) => (
            <li key={index} className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
              <span className="text-sm text-gray-700">{feature}</span>
            </li>
          ))}
        </ul>

        {isCurrent ? (
          <p className="mt-6 rounded-lg bg-gray-50 py-2.5 text-center text-sm font-medium text-gray-500">
            Current plan
          </p>
        ) : isFree ? (
          <p className="mt-6 rounded-lg bg-gray-50 py-2.5 text-center text-sm text-gray-500">
            Included with every account
          </p>
        ) : (
          <button
            onClick={() => subscribe(tier.name as StripeTierName)}
            disabled={checkoutLoading !== null}
            className="mt-6 rounded-lg bg-blue-600 px-5 py-2.5 font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50"
          >
            {checkoutLoading === tier.name ? "Opening checkout…" : "Choose"}
          </button>
        )}
        {!isFree && !isCurrent && (
          <RenewalTerms
            kind={tier.billingType === "one_time" ? "one_time" : "recurring"}
            price={`${currencySymbol}${tier.price}`}
            className="mt-3"
          />
        )}
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/traveler/subscription"
            className="mb-2 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 transition hover:text-gray-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Subscription
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Change plan</h1>
          <p className="mt-1 text-gray-500">
            Upgrade, or buy cover for a single trip. Cancel any time.
          </p>
        </div>

        <div className="inline-flex items-center gap-1 rounded-full bg-gray-100 p-1">
          {(["eur", "usd"] as Currency[]).map((option) => (
            <button
              key={option}
              onClick={() => setCurrency(option)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                currency === option
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {option === "eur" ? "€ EUR" : "$ USD"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid gap-4 md:grid-cols-3">
          <div className="h-72 animate-pulse rounded-xl bg-gray-100" />
          <div className="h-72 animate-pulse rounded-xl bg-gray-100" />
          <div className="h-72 animate-pulse rounded-xl bg-gray-100" />
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            {monthly.map((tier) =>
              renderCard(
                tier,
                tier.billingType === "free" ? "/forever" : "/month",
              ),
            )}
          </div>

          {singleTrip.length > 0 && (
            <div className="space-y-4 pt-2">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  One trip only
                </h2>
                <p className="text-sm text-gray-500">
                  A single payment that covers one journey, with no subscription
                  afterwards.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {singleTrip.map((tier) =>
                  renderCard(
                    tier,
                    tier.durationDays ? `/${tier.durationDays} days` : "once",
                  ),
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
